import * as THREE from 'three';

// Branching lightning bolts from the cloud base to the sea, drawn as glowing
// camera-facing ribbons with HDR intensity so bloom catches them.

const VERT = /* glsl */ `
in vec3 aDir;
in float aWidth;
out float vEdge;
out float vFade;
void main() {
  vec3 toCam = normalize(cameraPosition - position);
  vec3 side = normalize(cross(aDir, toCam));
  float w = aWidth * (1.0 + length(cameraPosition - position) * 0.0004);
  vec3 wp = position + side * w * uv.x;
  vEdge = uv.x;
  vFade = uv.y;
  gl_Position = projectionMatrix * viewMatrix * vec4(wp, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform float uFlash;
in float vEdge;
in float vFade;
void main() {
  float core = exp(-vEdge * vEdge * 6.0);
  vec3 col = vec3(0.75, 0.82, 1.0) * core * uFlash * 400.0 * vFade;
  gl_FragColor = vec4(col, 1.0);
}
`;

export class LightningBolts {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;
  private seed = 0;

  constructor() {
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: { uFlash: { value: 0 } },
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), this.material);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.mesh.renderOrder = 40;
  }

  strike(x: number, z: number, top: number, seed: number): void {
    this.seed = seed;
    const rand = () => {
      this.seed = (this.seed * 16807) % 2147483647;
      return this.seed / 2147483647;
    };
    const pos: number[] = [];
    const dir: number[] = [];
    const width: number[] = [];
    const uv: number[] = [];
    const idx: number[] = [];
    const addBranch = (start: THREE.Vector3, end: THREE.Vector3, w: number, depth: number, fade: number) => {
      // Midpoint displacement.
      let pts = [start, end];
      const len = start.distanceTo(end);
      for (let it = 0; it < 7; it++) {
        const next: THREE.Vector3[] = [pts[0]!];
        const amp = len * 0.12 / Math.pow(1.75, it);
        for (let i = 0; i < pts.length - 1; i++) {
          const m = new THREE.Vector3().lerpVectors(pts[i]!, pts[i + 1]!, 0.5);
          m.x += (rand() - 0.5) * amp * 2;
          m.z += (rand() - 0.5) * amp * 2;
          m.y += (rand() - 0.5) * amp * 0.5;
          next.push(m, pts[i + 1]!);
        }
        pts = next;
      }
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i]!, b = pts[i + 1]!;
        const d = new THREE.Vector3().subVectors(b, a).normalize();
        const base = pos.length / 3;
        const f = fade * (1 - (i / pts.length) * (depth > 0 ? 0.8 : 0.2));
        for (const [p, e] of [[a, -1], [a, 1], [b, -1], [b, 1]] as const) {
          pos.push(p.x, p.y, p.z);
          dir.push(d.x, d.y, d.z);
          width.push(w);
          uv.push(e, f);
        }
        idx.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
        if (depth < 2 && rand() < 0.035) {
          const bend = new THREE.Vector3((rand() - 0.5) * 1200, -rand() * 900 - 300, (rand() - 0.5) * 1200);
          addBranch(a, a.clone().add(bend), w * 0.5, depth + 1, f * 0.7);
        }
      }
    };
    const start = new THREE.Vector3(x + (rand() - 0.5) * 600, top, z + (rand() - 0.5) * 600);
    addBranch(start, new THREE.Vector3(x, 0, z), 7, 0, 1);
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('aDir', new THREE.Float32BufferAttribute(dir, 3));
    g.setAttribute('aWidth', new THREE.Float32BufferAttribute(width, 1));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx);
    this.mesh.geometry.dispose();
    this.mesh.geometry = g;
  }

  update(flash: number, active: boolean): void {
    this.material.uniforms.uFlash!.value = flash;
    this.mesh.visible = active && flash > 0.01;
  }
}
